import { INAUGURAL_SEASON } from './SeasonDefinition';
import { TOUR_GOLFERS } from './TourGolfers';
import type { HistoricalGolferCareer, TourGolfer, TourWorldHistory } from './types';

export const WORLD_HISTORY_SCHEMA_VERSION = 1;
const HISTORY_START_SEASON = -10;
const LAST_HISTORICAL_SEASON = 0;

const careerFor = (golfer: TourGolfer, index: number): HistoricalGolferCareer => {
  const seasonsPlayed = Math.max(1, Math.min(18, golfer.age - 20));
  const starts = seasonsPlayed * (12 + (index % 6));
  const rookie = seasonsPlayed <= 3;
  const topTens = Math.max(golfer.careerWins, Math.round(starts * (rookie ? 0.1 : 0.2)));
  return {
    golferId: golfer.id,
    professionalSinceSeason: LAST_HISTORICAL_SEASON - seasonsPlayed + 1,
    seasonsPlayed,
    careerStarts: starts,
    careerWins: golfer.careerWins,
    signatureWins: Math.min(golfer.careerWins, Math.floor(golfer.careerWins / 4)),
    majorStarts: Math.max(0, seasonsPlayed * 2 - (index % 4)),
    majorWins: golfer.majorWins,
    seasonChampionshipStarts: Math.max(0, seasonsPlayed - 2),
    seasonChampionshipWins: index === 7 || index === 13 ? 1 : 0,
    seasonTitles: index === 7 || index === 13 ? 1 : 0,
    runnerUpFinishes: Math.max(rookie ? 0 : 1, Math.floor(golfer.careerWins * 0.8)),
    topFiveFinishes: Math.max(golfer.careerWins, Math.round(topTens * 0.55)),
    topTenFinishes: topTens,
    cutsMade: Math.round(starts * (0.64 + golfer.consistency / 400)),
    cutsMissed: 0,
    careerBestRanking: Math.max(1, Math.min(golfer.currentRanking, (index % 12) + 1)),
    currentConsecutiveSeasonsWithWin: golfer.careerWins ? 1 + (index % 3) : 0,
    longestWinStreak:
      golfer.careerWins > 10 ? 4 : golfer.careerWins > 3 ? 2 : golfer.careerWins ? 1 : 0,
    notableEventWins: [],
    strongestCourses: [INAUGURAL_SEASON.events[index % 20].courseId],
    weakestCourses: [INAUGURAL_SEASON.events[(index + 9) % 20].courseId],
    recentResults: Array.from({ length: 5 }, (_, resultIndex) => ({
      eventId: INAUGURAL_SEASON.events[(15 + resultIndex) % 20].id,
      season: 0,
      position: 1 + ((index * 7 + resultIndex * 11) % 48),
      score: -15 + ((index + resultIndex * 3) % 20),
    })),
    careerEarnings: golfer.careerWins * 1_400_000 + starts * 54_000,
  };
};

/** Canonical, deterministic history. It is generated once at module load, never at career creation. */
export const CANONICAL_WORLD_HISTORY: TourWorldHistory = (() => {
  const golferCareerHistory = Object.fromEntries(
    TOUR_GOLFERS.map((golfer, index) => [golfer.id, careerFor(golfer, index)]),
  );
  const eventHistory = Object.fromEntries(
    INAUGURAL_SEASON.events.map((event, eventIndex) => {
      const pastChampions = Array.from({ length: 8 }, (_, offset) => {
        const season = LAST_HISTORICAL_SEASON - offset;
        const champion = TOUR_GOLFERS[(eventIndex * 5 + offset * 7 + (offset === 0 ? 3 : 0)) % 32];
        const runnerUp = TOUR_GOLFERS[(eventIndex * 5 + offset * 7 + 1) % 40];
        golferCareerHistory[champion.id].notableEventWins.push(event.id);
        return {
          id: `${event.id}:${season}`,
          season,
          championId: champion.id,
          runnerUpId: runnerUp.id,
          winningScore: -12 - ((eventIndex + offset) % 9),
          margin: 1 + ((eventIndex + offset) % 4),
        };
      });
      const counts = new Map<string, number>();
      pastChampions.forEach((result) =>
        counts.set(result.championId, (counts.get(result.championId) ?? 0) + 1),
      );
      return [
        event.id,
        {
          eventId: event.id,
          firstPlayedSeason: -24 - (eventIndex % 12),
          pastChampions,
          multipleWinners: [...counts]
            .filter(([, wins]) => wins > 1)
            .map(([golferId, wins]) => ({ golferId, wins })),
          defendingChampionId: pastChampions[0].championId,
          courseRecord: {
            golferId: pastChampions[3].championId,
            score: 61 + (eventIndex % 3),
            season: pastChampions[3].season,
          },
          tournamentScoringRecord: {
            golferId: pastChampions[2].championId,
            score: Math.min(...pastChampions.map((result) => result.winningScore)),
            season: pastChampions[2].season,
          },
          notableMoments: [
            {
              season: pastChampions[1].season,
              description: `A final-hole birdie decided the ${event.name}.`,
            },
          ],
        },
      ];
    }),
  );
  const pastSeasons = Array.from({ length: 11 }, (_, offset) => ({
    season: HISTORY_START_SEASON + offset,
    championId: TOUR_GOLFERS[(7 + offset * 5) % 18].id,
    championshipWinnerId: TOUR_GOLFERS[(13 + offset * 3) % 22].id,
    majorWinnerIds: [8, 17, 18].map(
      (majorIndex) =>
        eventHistory[INAUGURAL_SEASON.events[majorIndex].id].pastChampions[Math.min(10 - offset, 7)]
          .championId,
    ),
  }));
  return {
    schemaVersion: WORLD_HISTORY_SCHEMA_VERSION,
    historyStartSeason: HISTORY_START_SEASON,
    mostRecentCompletedSeason: LAST_HISTORICAL_SEASON,
    pastSeasons,
    eventHistory,
    golferCareerHistory,
    records: {
      careerWinLeaderId: TOUR_GOLFERS.reduce((a, b) => (a.careerWins > b.careerWins ? a : b)).id,
      majorWinLeaderId: TOUR_GOLFERS.reduce((a, b) => (a.majorWins > b.majorWins ? a : b)).id,
      lowestRound: 59,
    },
  };
})();

export const cloneCanonicalWorldHistory = (): TourWorldHistory =>
  structuredClone(CANONICAL_WORLD_HISTORY);
